<template>
  <div
    class="p-fileupload p-fileupload-advanced p-component"
    :class="{ empty: !hasFiles, 'download-mode': hasFiles && disabled }"
    ref="pFileupload"
  >
    <div
      class="p-fileupload-content"
      :class="contentClass"
      @dragenter="onDragEnter"
      @dragover="onDragOver"
      @dragleave="onDragLeave"
      @drop="onDrop"
      @click="!multiple && !externalPreview && downloadFile(files[0], $event)"
      ref="content"
    >
      <FileUploadProgressBar :value="progress" v-if="hasFiles && !customUpload" />
      <FileUploadMessage v-for="msg of messages" severity="error" :key="msg" :closable="false">
        {{ msg }}
      </FileUploadMessage>

      <div class="p-fileupload-empty" v-if="(!hasFiles || multiple) && !hideEmptyContainer">
        <slot name="empty">
          <icon
            data="@icon/upload-cloud.svg"
            width="3.5rem"
            height="3.5rem"
            :fill="true"
            class="file-upload-icon"
          />
          <p class="file-upload-label">
            Drag & Drop your file{{ multiple ? "s" : "" }} here to upload
          </p>
          <span v-if="fileSubtitle" class="file-upload-subtitle">{{ fileSubtitle }}</span>
        </slot>
        <div
          :class="advancedChooseButtonClass"
          @click="choose"
          @keydown.enter="choose"
          @focus="onFocus"
          @blur="onBlur"
          @dragenter="onDragEnter"
          @dragover="onDragOver"
          @dragleave="onDragLeave"
          @drop="onDrop"
          v-ripple
          tabindex="0"
          v-show="!hasFiles || multiple"
          title="Click to open file dialog"
        >
          <input
            ref="fileInput"
            type="file"
            @change="onFileSelect"
            :multiple="multiple"
            :accept="accept"
            :disabled="chooseDisabled"
          />
        </div>
      </div>
      <Skeleton v-if="hideEmptyContainer && !hasFiles" width="100%" height="1.25em" />
      <div class="p-fileupload-files" v-if="hasFiles">
        <div
          class="p-fileupload-row"
          :class="{ multiple: multiple }"
          v-for="(file, index) of files"
          :key="(file.name ?? '') + (file.type ?? '') + (file.size ?? '') + index"
          @click="multiple && downloadFile(file, $event)"
        >
          <div
            class="image-thumbnail p-mr-3 p-d-flex p-jc-center"
            v-if="(file?.objectURL && isImage(file)) || isPdf(file)"
          >
            <Image
              role="presentation"
              :alt="file.name"
              :src="file.objectURL"
              :width="previewWidth"
              preview
              :isImage="isImage(file)"
            />
          </div>
          <div class="p-d-flex p-flex-column p-flex-1 p-mb-0 p-mr-3 file-info" v-if="file.name">
            <h6 class="text-color-primary p-mt-0 p-mb-2" :class="{ 'p-my-0': hideEmptyContainer }">
              {{ file.name }}
            </h6>
            <span
              class="text-color-secondary p-text-nowrap p-mb-2"
              :class="{ 'p-my-0': hideEmptyContainer }"
            >
              {{ formatSize(file.size) }}
            </span>
          </div>
          <div class="file-actions">
            <FileUploadButton
              icon="pi pi-download"
              v-if="file && !externalPreview"
              title="Download"
              outlined
              rounded
            />
            <FileUploadButton
              icon="pi pi-times"
              class="p-ml-2"
              @click.stop="remove(index)"
              v-if="!disabled"
              title="Remove"
              rounded
              outlined
            />
          </div>
        </div>
      </div>
    </div>
    <slot name="actions" />
  </div>
</template>

<script>
import { addClass, removeClass } from "@primeuix/utils/dom";
import Button from "primevue/button";
import ProgressBar from "primevue/progressbar";
import Message from "primevue/message";
import Ripple from "primevue/ripple";
import Skeleton from "primevue/skeleton";
import Image from "@/components/elements/Image";

export default {
  name: "FileUpload",
  emits: [
    "select",
    "uploader",
    "before-upload",
    "progress",
    "upload",
    "error",
    "before-send",
    "clear",
  ],
  props: {
    name: {
      type: String,
      default: null,
    },
    url: {
      type: String,
      default: null,
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    accept: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    auto: {
      type: Boolean,
      default: false,
    },
    maxFileSize: {
      type: Number,
      default: 25 * 1000 * 1024, // bytes
    },
    invalidFileSizeMessage: {
      type: String,
      default: "{0}: Invalid file size, file size should be smaller than {1}.",
    },
    fileLimit: {
      type: Number,
      default: null,
    },
    invalidFileLimitMessage: {
      type: String,
      default: "Maximum number of files exceeded, limit is {0} at most.",
    },
    withCredentials: {
      type: Boolean,
      default: false,
    },
    previewWidth: {
      type: Number,
      default: 75,
    },
    chooseLabel: {
      type: String,
      default: null,
    },
    uploadLabel: {
      type: String,
      default: null,
    },
    cancelLabel: {
      type: String,
      default: null,
    },
    customUpload: {
      type: Boolean,
      default: true,
    },
    uploadedFiles: {
      type: Array,
      default: null,
    },
    contentClass: {
      type: [String, Object],
      default: null,
    },
    externalPreview: {
      type: Boolean,
      default: false,
    },
    hideEmptyContainer: {
      type: Boolean,
      default: false,
    },
    fileSubtitle: {
      type: String,
      default: "",
    },
  },
  duplicateIEEvent: false,
  data() {
    return {
      uploadedFileCount: 0,
      files: [],
      messages: [],
      focused: false,
      progress: null,
    };
  },
  methods: {
    onFileSelect(event) {
      if (event.type !== "drop" && this.isIE11() && this.duplicateIEEvent) {
        this.duplicateIEEvent = false;
        return;
      }

      this.messages = [];
      this.files = this.files || [];
      const files = event.dataTransfer ? event.dataTransfer.files : event.target.files;
      for (const file of files) {
        if (!this.isFileSelected(file)) {
          if (this.validate(file)) {
            file.objectURL = window.URL.createObjectURL(file);
            this.files.push(file);
          }
        }
      }

      this.$emit("select", { originalEvent: event, files: this.files });

      if (this.fileLimit) {
        this.checkFileLimit();
      }

      if (this.auto && this.hasFiles && !this.isFileLimitExceeded()) {
        this.upload();
      }

      if (event.type !== "drop" && this.isIE11()) {
        this.clearIEInput();
      } else {
        this.clearInputElement();
      }
    },
    choose() {
      this.$refs.fileInput.click();
    },
    upload() {
      if (this.customUpload) {
        if (this.fileLimit) {
          this.uploadedFileCount += this.files.length;
        }

        this.$emit("uploader", { files: this.files });
      } else {
        const xhr = new XMLHttpRequest();
        const formData = new FormData();

        this.$emit("before-upload", {
          xhr: xhr,
          formData: formData,
        });

        for (const file of this.files) {
          formData.append(this.name, file, file.name);
        }

        xhr.upload.addEventListener("progress", event => {
          if (event.lengthComputable) {
            this.progress = Math.round((event.loaded * 100) / event.total);
          }

          this.$emit("progress", {
            originalEvent: event,
            progress: this.progress,
          });
        });

        xhr.onreadystatechange = () => {
          if (xhr.readyState === 4) {
            this.progress = 0;

            if (xhr.status >= 200 && xhr.status < 300) {
              if (this.fileLimit) {
                this.uploadedFileCount += this.files.length;
              }

              this.$emit("upload", {
                xhr: xhr,
                files: this.files,
              });
            } else {
              this.$emit("error", {
                xhr: xhr,
                files: this.files,
              });
            }

            this.clear();
          }
        };

        xhr.open("POST", this.url, true);

        this.$emit("before-send", {
          xhr: xhr,
          formData: formData,
        });

        xhr.withCredentials = this.withCredentials;

        xhr.send(formData);
      }
    },
    clear() {
      this.files = [];
      this.messages = null;
      this.$emit("clear");
      this.clearInputElement();
    },
    onFocus() {
      this.focused = true;
    },
    onBlur() {
      this.focused = false;
    },
    isFileSelected(file) {
      if (this.files && this.files.length) {
        for (const sFile of this.files) {
          if (sFile.name + sFile.type + sFile.size === file.name + file.type + file.size)
            return true;
        }
      }

      return false;
    },
    isIE11() {
      return !!window["MSInputMethodContext"] && !!document["documentMode"];
    },
    validate(file) {
      if (this.maxFileSize && file.size > this.maxFileSize) {
        this.messages.push(
          this.invalidFileSizeMessage
            .replace("{0}", file.name)
            .replace("{1}", this.formatSize(this.maxFileSize)),
        );
        return false;
      }

      return true;
    },
    onDragEnter(event) {
      if (!this.disabled) {
        event.stopPropagation();
        event.preventDefault();
      }
    },
    onDragOver() {
      if (!this.disabled) {
        addClass(this.$refs.pFileupload, "p-fileupload-highlight");
        event.stopPropagation();
        event.preventDefault();
      }
    },
    onDragLeave() {
      if (!this.disabled) {
        removeClass(this.$refs.pFileupload, "p-fileupload-highlight");
      }
    },
    onDrop() {
      if (!this.disabled) {
        removeClass(this.$refs.pFileupload, "p-fileupload-highlight");
        event.stopPropagation();
        event.preventDefault();

        const files = event.dataTransfer ? event.dataTransfer.files : event.target.files;
        const allowDrop = this.multiple || (files && files.length === 1);

        if (allowDrop) {
          this.onFileSelect(event);
        }
      }
    },
    remove(index) {
      this.clearInputElement();
      this.files.splice(index, 1);
      this.files = [...this.files];
      if (this.files.length === 0) {
        this.clear();
      } else {
        this.$emit("select", { files: this.files });
      }
    },
    isImage(file) {
      return /^image\//.test(file.type);
    },
    isPdf(file) {
      return /pdf/.test(file.type);
    },
    clearInputElement() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },
    clearIEInput() {
      if (this.$refs.fileInput) {
        this.duplicateIEEvent = true; //IE11 fix to prevent onFileChange trigger again
        this.$refs.fileInput.value = "";
      }
    },
    formatSize(bytes) {
      if (bytes === 0) {
        return "0 B";
      }
      const k = 1000,
        dm = 3,
        sizes = ["B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"],
        i = Math.floor(Math.log(bytes) / Math.log(k));

      return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
    },
    isFileLimitExceeded() {
      if (
        this.fileLimit &&
        this.fileLimit <= this.files.length + this.uploadedFileCount &&
        this.focused
      ) {
        this.focused = false;
      }

      return this.fileLimit && this.fileLimit < this.files.length + this.uploadedFileCount;
    },
    checkFileLimit() {
      if (this.isFileLimitExceeded()) {
        this.messages.push(this.invalidFileLimitMessage.replace("{0}", this.fileLimit.toString()));
      }
    },
    downloadFile(file, e) {
      if (file && !e.target.className.includes("p-image-preview-icon")) {
        const a = document.createElement("a");
        a.href = file.objectURL;
        a.download = file.name;
        a.click();
      }
    },
  },
  computed: {
    advancedChooseButtonClass() {
      return [
        "p-button p-component p-fileupload-choose",
        {
          "p-focus": this.focused,
          "p-disabled": this.disabled,
          multiple: this.multiple,
        },
      ];
    },
    hasFiles() {
      return this.files && this.files.length > 0;
    },
    chooseDisabled() {
      return (
        this.disabled ||
        (this.fileLimit && this.fileLimit <= this.files.length + this.uploadedFileCount)
      );
    },
    uploadDisabled() {
      return this.disabled || !this.hasFiles;
    },
    cancelDisabled() {
      return this.disabled || !this.hasFiles;
    },
    chooseButtonLabel() {
      return this.chooseLabel || this.$primevue.config.locale.choose;
    },
    uploadButtonLabel() {
      return this.uploadLabel || this.$primevue.config.locale.upload;
    },
    cancelButtonLabel() {
      return this.cancelLabel || this.$primevue.config.locale.cancel;
    },
  },
  components: {
    FileUploadButton: Button,
    FileUploadProgressBar: ProgressBar,
    FileUploadMessage: Message,
    Image,
    Skeleton,
  },
  directives: {
    ripple: Ripple,
  },
  watch: {
    uploadedFiles(newFiles) {
      if (newFiles?.length > 0) {
        this.files = newFiles;
      }
    },
  },
};
</script>

<style lang="scss">
.p-fileupload.p-fileupload-advanced {
  position: relative;

  &:hover,
  &:focus,
  &.p-fileupload-highlight {
    .p-fileupload-content {
      border-color: var(--primary-color);
    }
  }

  &.p-invalid {
    .p-fileupload-content {
      border-color: inherit;
      color: inherit;
    }
  }

  .p-fileupload-content {
    position: relative;
    background: var(--element-bg);
    border: dashed 1px var(--element-bg-dark);
    border-radius: var(--border-radius-sm);
    transition: border-color 0.2s;
    padding: 2rem 1rem;
  }

  .p-fileupload-choose {
    position: absolute;
    top: -2rem;
    left: -1rem;
    width: calc(100% + 2rem);
    height: calc(100% + 4rem);
    z-index: 1;
    opacity: 0;
  }

  .p-fileupload-empty {
    position: relative;
    text-align: center;
    color: var(--element-bg-dark);
    p {
      margin: 0;
    }
    h4 {
      color: var(--text-color);
    }
  }

  &.download-mode {
    cursor: pointer;
  }
}

.p-fileupload-row {
  display: flex;
  align-items: center;

  &.multiple {
    border-top: solid 1px var(--element-border);
    padding: 16px 0;
    &:first-child {
      margin-top: 2rem;
    }
  }
}

.p-fileupload-content .p-progressbar {
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
}
.p-fileupload-content h6 {
  line-height: 1.5rem;
  word-break: break-word;
}

.p-button.p-fileupload-choose {
  position: relative;
  overflow: hidden;
}

.p-button.p-fileupload-choose input[type="file"] {
  display: none;
}

.p-fileupload-choose.p-fileupload-choose-selected input[type="file"] {
  display: none;
}
</style>
